import React, { useState } from 'react';
import { useLanguage } from '../../../context/LanguageContext';
import { supabase } from '../../../config/supabase';
import { useAuth } from '../../../context/AuthContext';
import Modal from '../../shared/components/Modal';
import { sendTelegramNotification } from '../../shared/utils/helpers';
import { buildTelegramMessage } from '../../../utils/telegramMessages';

export default function ClientPaymentModal({ client, onClose, onSave }) {
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('Espèces');
  const { lang, t } = useLanguage();
  const { agencyId } = useAuth();
  const isAr = lang === 'ar';
  const currentBalance = client.creditBalance || 0;
  
  const submit = async () => {
    const val = parseFloat(amount);
    if (!val || val <= 0) {
      return alert(isAr ? 'يرجى إدخال مبلغ صالح' : 'Veuillez entrer un montant valide');
    }
    if (val > currentBalance) {
      return alert(isAr ? 'المبلغ المدفوع أكبر من الدين الحالي' : 'Le montant payé est supérieur au crédit existant');
    }
    
    const newBalance = currentBalance - val;
    const timestamp = new Date().toISOString();
    const paymentDate = timestamp.split('T')[0];
    
    try {
      // 1. Update client balance in Supabase
      const { data: existingClient } = await supabase
        .from('clients')
        .select('data')
        .eq('id', client.id)
        .single();
        
      const mergedClient = { 
        ...(existingClient?.data || {}), 
        creditBalance: newBalance,
        lastPaymentDate: paymentDate 
      };
      
      const { error: clientErr } = await supabase
        .from('clients')
        .update({ data: mergedClient })
        .eq('id', client.id);
        
      if (clientErr) throw clientErr;
      
      // 2. Save payment to history
      const paymentPayload = {
        clientId: client.id,
        clientName: client.name,
        amount: val,
        previousBalance: currentBalance,
        newBalance: newBalance,
        paymentMethod: paymentMethod,
        note: note || '',
        date: paymentDate,
        timestamp: timestamp,
        createdBy: 'manual'
      };
      
      const { error: payErr } = await supabase
        .from('client_payments')
        .insert({
          agency_id: agencyId,
          created_at: timestamp,
          data: paymentPayload
        });
        
      if (payErr) throw payErr;
      
      // Telegram notification
      try {
        const saved = localStorage.getItem('appSettings');
        let currency = 'MAD';
        if (saved) {
          try {
            const settings = JSON.parse(saved);
            currency = settings.currency || 'MAD';
          } catch (_) {}
        }
        await sendTelegramNotification(
          buildTelegramMessage('client_payment', {
            clientName: client.name, amount: val, currency,
            method: paymentMethod, note, balance: newBalance
          }),
          'client_payment'
        );
      } catch (tgErr) {
        console.error('Failed to send payment Telegram notification:', tgErr);
      }
      
      alert(
        isAr 
          ? `✅ تم تسجيل دفع ${val} درهم بنجاح!\nالرصيد المتبقي: ${newBalance} درهم`
          : `✅ Paiement de ${val} MAD enregistré!\nNouveau solde: ${newBalance} MAD`
      );
      
      if (onSave) onSave();
      onClose();
    } catch (err) {
      alert('❌ Erreur: ' + err.message);
    }
  };

  return (
    <Modal onClose={onClose} title={(isAr ? 'تسجيل دفع' : 'Enregistrer un paiement')}>
      <div>
        {/* Client info */}
        <div className="mb-5 p-4 bg-gradient-to-r from-red-50 to-orange-50 border-2 border-red-200 rounded-xl">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-xs text-gray-600 font-bold uppercase">{isAr ? 'العميل' : 'Client'}</p>
              <p className="text-lg font-bold text-gray-900">{client.name}</p>
              {client.phone && <p className="text-xs text-gray-500 font-mono mt-0.5">📞 {client.phone}</p>}
            </div>
            <div className="text-right">
              <p className="text-xs text-gray-600 font-bold uppercase">{isAr ? 'الدين الحالي' : 'Crédit actuel'}</p>
              <p className="text-2xl font-black text-red-600">{currentBalance} MAD</p>
            </div>
          </div>
        </div>

        
        {/* Amount input */}
        <div className="mb-4">
          <label className="block text-sm font-bold text-gray-700 mb-1">
            💵 {isAr ? 'المبلغ المدفوع' : 'Montant payé'}
          </label>
          <input
            type="number"
            value={amount}
            onChange={e => setAmount(e.target.value)}
            placeholder="0"
            max={currentBalance}
            step="0.01"
            className="w-full px-4 py-3 text-2xl font-bold border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          {amount && parseFloat(amount) > 0 && (
            <p className="text-xs mt-1.5 text-gray-600">
              {isAr ? 'الرصيد المتبقي بعد الدفع: ' : 'Solde restant après paiement: '}
              <strong className="text-green-700">
                {(currentBalance - parseFloat(amount)).toFixed(2)} MAD
              </strong>
            </p>
          )}
        </div>
        
        {/* Payment method */}
        <div className="mb-4">
          <label className="block text-sm font-bold text-gray-700 mb-1">
            💳 {isAr ? 'طريقة الدفع' : 'Mode de paiement'}
          </label>
          <select
            value={paymentMethod}
            onChange={e => setPaymentMethod(e.target.value)}
            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500"
          >
            <option value="Espèces">{isAr ? 'نقداً' : 'Espèces'}</option>
            <option value="Chèque">{isAr ? 'شيك' : 'Chèque'}</option>
            <option value="Virement">{isAr ? 'تحويل بنكي' : 'Virement'}</option>
            <option value="Carte">{isAr ? 'بطاقة' : 'Carte bancaire'}</option>
            <option value="Autre">{isAr ? 'أخرى' : 'Autre'}</option>
          </select>
        </div>
        
        {/* Note */}
        <div className="mb-4">
          <label className="block text-sm font-bold text-gray-700 mb-1">
            📝 {isAr ? 'ملاحظة (اختياري)' : 'Note (optionnel)'}
          </label>
          <input
            type="text"
            value={note}
            onChange={e => setNote(e.target.value)}
            placeholder={isAr ? 'مثلا: دفعة جزئية...' : 'Ex: Paiement partiel, reçu n°...'}
            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          />
        </div>
        
        <div className="flex gap-3 mt-6">
          <button 
            onClick={onClose} 
            className="flex-1 px-4 py-3 border border-gray-300 rounded-xl font-bold hover:bg-gray-50 text-gray-700 transition" 
          >
            {isAr ? 'إلغاء' : 'Annuler'}
          </button>
          <button 
            onClick={submit} 
            disabled={!amount || parseFloat(amount) <= 0}
            className={`flex-1 px-4 py-3 rounded-xl font-bold shadow-md flex items-center justify-center gap-2 transition ${
              amount && parseFloat(amount) > 0
                ? 'bg-green-600 hover:bg-green-700 text-white' 
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            💰 {isAr ? 'تأكيد الدفع' : 'Valider le paiement'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
